import { AppModule } from "src/app/app.module";
import { MineSort } from "../mine-sort/mine-sort.interface";
import { SortDirectionEnum } from "../sort-direction.enum";
import { TableState } from "./table-state.interface";
import { TableStateService } from "./table-state.service";
import { TableElement, TablesEnum } from "./table-state.enum";

import { EmployeesTableColumnKeys } from "src/app/employees/employees-managment/employees-table/employees-table.enum";
import { ImpactAssessmentTableHeaderEnum } from "src/app/impact-assessment/models/Impact-assessment.interface";
import { RadarColumnKeys } from "src/app/radar/radar-table/radar-table.enum";
import { RequestSortKey } from "src/app/requests/models/request-sort-key.enum";
import { SystemsColumnKeys } from "src/app/systems/models/systems-column.enum";
import { TrashSystemsColumnKeys } from "src/app/unused-assets/trash-systems/trash-systems.enum";
import { PiiClassifierColumnKeys } from "src/app/pii-classifier/models/pii-classifier-column.enum";
import { PiiSystemSchemaColumnsEnum } from "src/app/pii-classifier/pii-system-findings/pii-system-schema/schema-table-header/schema-table-header.enum";
import { PiiSystemDataTypeColumnsEnum } from "src/app/pii-classifier/pii-system-findings/pii-system-data-types/data-types-table-header/data-types.enum";
import { CustomValuesTableColumns } from "src/app/company-settings/customization/custom-values/custom-values.enum";
import { LocalizationColumns } from "src/app/dsr/dsr-setup/dsr-localization/models/localization-colums.enum";
import { TemplatesColumns } from "../../dsr/dsr-setup/dsr-templates/models/templates-columns.enum";
import { CompanyGroupsTableColumns } from "src/app/company-settings/groups/groups.enum";
import { AssessmentColumnEnum } from "src/app/api/models/assessments/assessment.enum";
import { isCollaborationRoute } from "src/app/collaboration/collaborations.utils";
import { LoggerService } from "src/app/logger/logger.service";

export type TableKeyResolver = () => TablesEnum;

export function PreserveTableState(key: TablesEnum, state: any, dynamicKeyResolver?: TableKeyResolver): ClassDecorator {
    return function (constructor: any) {
        const loggerName = 'PreserveTableStateDecorator';

        if(!isCollaborationRoute()) {
            const tableStateService = AppModule.injector.get(TableStateService);
            const originalNgOnDestroy = constructor.prototype.ngOnDestroy;
            const logger = AppModule.injector.get(LoggerService);
    
            // Override ngOnDestory function with this custom function
            constructor.prototype.ngOnDestroy = function() {
                let tableKey = key;
                // Only attempt dynamic resolution if a resolver is provided. Bind the destroying component
                // as `this` so the resolver can read state captured at INIT (e.g. its route use-case) — at
                // ngOnDestroy the live root ActivatedRoute may already point to the NEXT page, which would
                // otherwise save this table's state under the wrong key (cross-page state leak).
                if (dynamicKeyResolver) {
                    try {
                        tableKey = dynamicKeyResolver.call(this);
                    }
                    catch (error) {
                        const logger = AppModule.injector.get(LoggerService);
                        logger.warn('PreserveTableState', `Dynamic table key resolution failed, using base key: ${error}`);
                        tableKey = key;
                    }
                }

                let tableState = this[state] as TableState;
                if (tableState.sort?.direction === SortDirectionEnum.Default || 
                    JSON.stringify(getTableDefaultSort(tableKey) ?? '') === JSON.stringify(tableState[TableElement.Sort] ?? '')) {
                    
                    // unset sort if it equals to the default sort OR direction is default
                    tableState[TableElement.Sort] = undefined;
                }
    
                logger.info(loggerName, `Save table state: ${tableKey}`);
                tableStateService.setTableState(tableKey, tableState);
                originalNgOnDestroy?.apply(this, arguments); // preserve the original ngOnDestory functionallity
                // originalNgOnDestroy?.apply(); // preserve the original ngOnDestory functionallity
            };
        };
    };
}

export function getTableDefaultSort(table: TablesEnum): MineSort {
	return TableDefaultSortMap[table] ?? {} as MineSort;
}

const TableDefaultSortMap: Partial<Record<TablesEnum, MineSort>> = {
	[TablesEnum.Employees]: { active: EmployeesTableColumnKeys.CoworkerDataSources, direction: SortDirectionEnum.Desc },
	[TablesEnum.UnusedEmployees]: { active: EmployeesTableColumnKeys.CoworkerDataSources, direction: SortDirectionEnum.Desc },
	[TablesEnum.Radar]: { active: RadarColumnKeys.Employees, direction: SortDirectionEnum.Desc },
	[TablesEnum.Requests]: { active: RequestSortKey.CreatedAt, direction: SortDirectionEnum.Desc },
	[TablesEnum.PiiClassifier]: { active: PiiClassifierColumnKeys.LastScan, direction: SortDirectionEnum.Desc },
	[TablesEnum.PiiClassifierSchema]: { active: PiiSystemSchemaColumnsEnum.MatchesFound, direction: SortDirectionEnum.Desc },
	[TablesEnum.PiiClassifierDataTypes]: { active: PiiSystemDataTypeColumnsEnum.MatchesFound, direction: SortDirectionEnum.Desc },
	[TablesEnum.Systems]: { active: SystemsColumnKeys.DiscoveryDate, direction: SortDirectionEnum.Desc },
	[TablesEnum.ImpactAssessment]: { active: ImpactAssessmentTableHeaderEnum.EstimatedImpact, direction: SortDirectionEnum.Desc },
	[TablesEnum.TrashSystems]: { active: TrashSystemsColumnKeys.LastUpdate, direction: SortDirectionEnum.Desc },
	[TablesEnum.CustomValues]: { active: CustomValuesTableColumns.Field, direction: SortDirectionEnum.Asc },
	[TablesEnum.CustomFields]: { active: CustomValuesTableColumns.UsedIn, direction: SortDirectionEnum.Asc },
	[TablesEnum.DsrLocalization]: { active: LocalizationColumns.Language, direction: SortDirectionEnum.Asc },
	[TablesEnum.DsrTemplates]: { active: TemplatesColumns.Name, direction: SortDirectionEnum.Asc },
	[TablesEnum.CompanyGroups]: { active: CompanyGroupsTableColumns.Name, direction: SortDirectionEnum.Asc },
	[TablesEnum.Assessments]: { active: AssessmentColumnEnum.LastUpdate, direction: SortDirectionEnum.Desc },
	[TablesEnum.AssessmentsRopa]: { active: AssessmentColumnEnum.LastUpdate, direction: SortDirectionEnum.Desc },
	[TablesEnum.AssessmentsPrivacy]: { active: AssessmentColumnEnum.LastUpdate, direction: SortDirectionEnum.Desc },
	[TablesEnum.AssessmentsVendor]: { active: AssessmentColumnEnum.LastUpdate, direction: SortDirectionEnum.Desc },
	[TablesEnum.AssessmentsAi]: { active: AssessmentColumnEnum.LastUpdate, direction: SortDirectionEnum.Desc },
};
